import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money, fmtDate } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { StatusBadge, Spinner } from "@/components/shared";
import { ArrowLeft, Mail, Phone, Wallet, TrendingUp, Archive } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value, accent = "text-slate-900" }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`font-mono-fin text-xl font-bold mt-1 ${accent}`}>{value}</p>
  </div>
);

export default function ConsignorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { store } = useStore();
  const [c, setC] = useState(null);
  const cur = store?.currency || "USD";

  const load = () => api.get(`/consignors/${id}`).then((r) => setC(r.data));
  useEffect(() => { load(); }, [id]);

  const archive = async () => {
    await api.post(`/consignors/${id}/archive`);
    toast.success("Consignor archived"); load();
  };

  if (!c) return <Spinner />;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/consignors")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-consignors">
        <ArrowLeft className="w-4 h-4" /> Back to consignors
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center text-white text-2xl font-bold font-display">{c.name.charAt(0)}</div>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-slate-900">{c.name}</h1>
            <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {c.email || "—"}</span>
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {c.phone || "—"}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={c.status} />
          {c.status !== "archived" && (
            <button data-testid="archive-consignor-btn" onClick={archive} className="flex items-center gap-1 text-sm text-slate-600 border border-slate-300 px-3 py-1.5 rounded-lg hover:bg-slate-100">
              <Archive className="w-4 h-4" /> Archive
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Payout %" value={`${c.default_payout_pct}%`} />
        <Stat label="Unpaid Balance" value={money(c.pending_balance, cur)} accent="text-amber-700" />
        <Stat label="Lifetime Sales" value={money(c.lifetime_sales, cur)} accent="text-emerald-700" />
        <Stat label="Lifetime Payouts" value={money(c.lifetime_payouts, cur)} accent="text-emerald-700" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Items ({c.items.length})</h3>
            <div className="text-xs text-slate-500">Active {c.active_items} · Sold {c.sold_items} · Expired {c.expired_items}</div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {c.items.map((it) => (
              <div key={it.id} className="px-4 py-2.5 border-b border-slate-50 flex items-center justify-between text-sm">
                <div><p className="font-medium text-slate-800">{it.product_name}</p><p className="text-xs text-slate-400">{money(it.current_price, cur)} · exp {fmtDate(it.expiration_date)}</p></div>
                <StatusBadge status={it.status} />
              </div>
            ))}
            {c.items.length === 0 && <p className="text-sm text-slate-400 p-4">No items.</p>}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-semibold text-slate-800">Settlement History</h3></div>
          <div className="max-h-96 overflow-y-auto">
            {c.settlements.map((s) => (
              <div key={s.id} className="px-4 py-2.5 border-b border-slate-50 flex items-center justify-between text-sm">
                <div><p className="font-medium text-slate-800">{s.period_label}</p><p className="text-xs text-slate-400">{s.items_sold} items · {money(s.net_sales, cur)} net</p></div>
                <div className="text-right"><p className="font-mono-fin font-semibold text-slate-900">{money(s.amount_owed, cur)}</p><StatusBadge status={s.status} className="mt-1" /></div>
              </div>
            ))}
            {c.settlements.length === 0 && <p className="text-sm text-slate-400 p-4">No settlements yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
